/* @img-migrated */
import React, { useState, useRef, useCallback } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

import Img from './Img';
import { resolveImg } from '../lib/images';
/**
 * PostImageCarousel — responsive image carousel for posts & blogs.
 *
 * Design goals (post RC-FEED-1):
 *   - Media wrapper collapses to the ACTUAL rendered image height — never
 *     leaves blank space between the image and the reactions bar.
 *   - Single image: renders at its natural aspect via w-auto/h-auto with a
 *     max-height cap. Portrait flyers letterbox on the SIDES (never below).
 *   - Multi image: frame adapts to the active slide's natural ratio, clamped
 *     to a comfortable band so navigating between mixed orientations doesn't
 *     cause extreme page jumps.
 *   - Loading skeleton uses a mild 4:5 placeholder to keep initial CLS low,
 *     then snaps to the true image height once loaded.
 *
 * Props:
 *   images         - string[] of image URLs (required)
 *   rounded        - Tailwind rounding class (default '')
 *   onImageClick   - callback(url, index) — optional lightbox hook
 *   objectFit      - 'cover' | 'contain' (default 'contain'; 'cover' respected)
 *   maxHeight      - cap for single-image + multi-image rendering (default 'max-h-[560px]')
 *   dataTestId     - test id prefix (default 'post-carousel')
 */
export const PostImageCarousel = ({
  images = [],
  rounded = '',
  onImageClick,
  objectFit = 'contain',
  maxHeight = 'max-h-[560px]',
  dataTestId = 'post-carousel',
}) => {
  const [idx, setIdx] = useState(0);
  const [singleLoaded, setSingleLoaded] = useState(false);
  const [aspects, setAspects] = useState({}); // { [i]: ratio }
  const touchStart = useRef(null);
  const touchEnd = useRef(null);
  const rootRef = useRef(null);

  const safeIdx = Math.max(0, Math.min(idx, images.length - 1));

  const go = useCallback((i) => {
    if (i < 0 || i >= images.length) return;
    setIdx(i);
  }, [images.length]);

  const prev = useCallback(() => go(safeIdx - 1), [go, safeIdx]);
  const next = useCallback(() => go(safeIdx + 1), [go, safeIdx]);

  const onTouchStart = (e) => {
    touchEnd.current = null;
    touchStart.current = e.targetTouches[0].clientX;
  };
  const onTouchMove = (e) => {
    touchEnd.current = e.targetTouches[0].clientX;
  };
  const onTouchEnd = () => {
    if (touchStart.current == null || touchEnd.current == null) return;
    const dx = touchStart.current - touchEnd.current;
    const threshold = 40;
    if (dx > threshold) next();
    else if (dx < -threshold) prev();
    touchStart.current = null;
    touchEnd.current = null;
  };

  const onKeyDown = (e) => {
    if (e.key === 'ArrowLeft') { e.preventDefault(); prev(); }
    else if (e.key === 'ArrowRight') { e.preventDefault(); next(); }
  };

  const fitClass = objectFit === 'cover' ? 'object-cover' : 'object-contain';

  if (!images || images.length === 0) return null;

  const captureAspect = (i, ev) => {
    const { naturalWidth: w, naturalHeight: h } = ev.currentTarget;
    if (w && h) {
      setAspects((prev) => (prev[i] ? prev : { ...prev, [i]: w / h }));
    }
  };

  // ── Single image ─────────────────────────────────────────────────────
  // The image itself drives layout (max-w-full, w-auto/h-auto, max-h-*).
  // Wrapper is a flex-centered box with NO fixed height — it hugs the image
  // exactly. Portrait flyers letterbox horizontally (side gray strips) but
  // never leave blank space BELOW the image. A skeleton with a mild 4:5
  // aspect ratio holds space while the image loads to minimize CLS.
  if (images.length === 1) {
    const url = images[0];
    return (
      <div
        className={`relative w-full ${rounded} overflow-hidden bg-slate-50 flex items-center justify-center`}
        data-testid={dataTestId}
        style={!singleLoaded ? { aspectRatio: '4 / 5' } : undefined}
      >
        {!singleLoaded && (
          <div
            className="absolute inset-0 bg-gradient-to-br from-slate-100 to-slate-200 animate-pulse"
            aria-hidden="true"
          />
        )}
        <Img
          variant="generic"
          src={resolveImg(url)}
          alt=""
          className={`relative block w-auto h-auto max-w-full ${maxHeight} ${fitClass} ${onImageClick ? 'cursor-pointer' : ''}`}
          loading="lazy"
          decoding="async"
          onLoad={() => setSingleLoaded(true)}
          onError={() => setSingleLoaded(true)}
          onClick={onImageClick ? () => onImageClick(url, 0) : undefined}
          data-testid={`${dataTestId}-img-0`}
        />
      </div>
    );
  }

  // ── Multi-image carousel ─────────────────────────────────────────────
  // Frame's aspect ratio follows the active slide (clamped between 3:4 and
  // 16:9 to avoid dramatic layout jumps). Smoothly transitions when the
  // user swipes between mixed orientations.
  const MIN_RATIO = 3 / 4;   // tall portrait — anything taller letterboxes
  const MAX_RATIO = 16 / 9;  // wide landscape — anything wider letterboxes
  const activeAspect = aspects[safeIdx];
  const clampedAspect = activeAspect
    ? Math.min(Math.max(activeAspect, MIN_RATIO), MAX_RATIO)
    : 4 / 5;

  return (
    <div
      ref={rootRef}
      className={`relative w-full select-none ${rounded} overflow-hidden bg-slate-100`}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      onKeyDown={onKeyDown}
      tabIndex={0}
      role="group"
      aria-roledescription="carousel"
      aria-label={`${images.length} images`}
      data-testid={dataTestId}
    >
      {/* Frame with dynamic aspect ratio — adapts to the active slide */}
      <div
        className={`relative w-full ${maxHeight} overflow-hidden transition-[aspect-ratio] duration-200`}
        style={{ aspectRatio: String(clampedAspect) }}
      >
        <div
          className="absolute inset-0 flex transition-transform duration-300 ease-out"
          style={{ transform: `translateX(-${safeIdx * 100}%)`, willChange: 'transform' }}
        >
          {images.map((img, i) => (
            <div
              key={`${img}-${i}`}
              className="flex-shrink-0 w-full h-full bg-slate-100"
            >
              {/* Image fills the frame (w-full h-full); object-contain preserves
                  the natural aspect and letterboxes only when the slide aspect
                  differs from the frame aspect. */}
              <Img
                variant="generic"
                src={resolveImg(img)}
                alt=""
                className={`w-full h-full ${fitClass} ${onImageClick ? 'cursor-pointer' : ''}`}
                loading={i === 0 ? 'eager' : 'lazy'}
                decoding="async"
                onLoad={(e) => captureAspect(i, e)}
                onClick={onImageClick ? () => onImageClick(img, i) : undefined}
                draggable={false}
                data-testid={`${dataTestId}-img-${i}`}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Counter badge */}
      <div
        className="absolute top-3 right-3 px-2 py-0.5 rounded-full bg-[#002249]/75 text-white text-[11px] font-semibold backdrop-blur-sm"
        data-testid={`${dataTestId}-counter`}
      >
        {safeIdx + 1} / {images.length}
      </div>

      {/* Prev / Next — hidden on mobile (swipe only), visible from sm+ */}
      {safeIdx > 0 && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); prev(); }}
          className="hidden sm:flex absolute left-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/90 hover:bg-white text-[#1a1a1a] items-center justify-center shadow-lg transition-all"
          aria-label="Previous image"
          data-testid={`${dataTestId}-prev`}
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
      )}
      {safeIdx < images.length - 1 && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); next(); }}
          className="hidden sm:flex absolute right-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/90 hover:bg-white text-[#1a1a1a] items-center justify-center shadow-lg transition-all"
          aria-label="Next image"
          data-testid={`${dataTestId}-next`}
        >
          <ChevronRight className="w-5 h-5" />
        </button>
      )}

      {/* Dots */}
      <div className="absolute bottom-3 left-0 right-0 flex justify-center gap-1.5 pointer-events-none">
        {images.map((_, i) => (
          <button
            key={i}
            type="button"
            onClick={(e) => { e.stopPropagation(); go(i); }}
            className={`pointer-events-auto transition-all rounded-full ${
              i === safeIdx
                ? 'bg-white w-5 h-1.5 shadow'
                : 'bg-white/65 w-1.5 h-1.5 hover:bg-white/85'
            }`}
            aria-label={`Go to image ${i + 1}`}
            data-testid={`${dataTestId}-dot-${i}`}
          />
        ))}
      </div>
    </div>
  );
};

export default PostImageCarousel;
